<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{ $t("acceptance.pra.receipt.dialog.title") }}
      </v-card-title>
      <v-card-subtitle class="pra-wrap">
        {{ subtitle }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.number"
              variant="outlined"
              density="comfortable"
              :maxlength="maxNumber"
              :label="`${$t('acceptance.pra.receipt.number')} *`"
              :error-messages="message('number')"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.date"
              type="date"
              variant="outlined"
              density="comfortable"
              :min="limits.min"
              :max="limits.max"
              :label="`${$t('acceptance.pra.receipt.date')} *`"
              :hint="dateHint"
              persistent-hint
              :error-messages="message('date')"
              :disabled="saving"
            />
          </v-col>
        </v-row>

        <div class="text-body-2 mt-4 mb-1">
          {{ $t("acceptance.pra.receipt.dialog.file") }} *
        </div>
        <div class="pra-file-box d-flex flex-wrap align-center ga-2 pa-3">
          <v-chip v-if="form.file" size="small" variant="tonal" color="primary">
            {{ form.file.name }} · {{ fileSize }}
          </v-chip>
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            :disabled="saving"
            @click="pickFile"
          >
            {{
              form.file
                ? $t("acceptance.pra.crd.dialog.change_file")
                : $t("acceptance.pra.crd.dialog.choose_file")
            }}
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            :accept="accept"
            @change="onFile"
          />
        </div>
        <div
          class="text-caption mt-1"
          :class="fileProblem ? 'text-error' : 'text-grey-darken-1'"
        >
          {{ fileProblem || $t("acceptance.pra.crd.dialog.file_hint") }}
        </div>

        <!-- The receipt is the only way to close the case (Radiata). -->
        <v-alert
          type="success"
          variant="tonal"
          density="compact"
          :icon="false"
          class="mt-4"
        >
          {{ $t("acceptance.pra.receipt.dialog.closing_note") }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.pra.crd.dialog.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="submit"
        >
          {{ $t("acceptance.pra.receipt.dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import { FILE_TYPES } from "@/components/Acceptance/dossierFiles.mjs";
import { formatFileSize } from "@/components/Acceptance/praCrdForm.mjs";
import {
  RECEIPT_NUMBER_MAX,
  emptyReceiptForm,
  receiptDateLimits,
  receiptFormErrors,
} from "@/components/Acceptance/praReceiptForm.mjs";

// "Registra ricevuta di radiazione" (Stage 2 slice 7): number, date and the receipt file, all
// required. Saving closes the case as Radiata. The page sends it through the case session; a save
// error keeps the dialog open with the input and the server message.
export default {
  name: "AcceptancePraReceiptDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Save failure from the page, shown inside the dialog; the input is kept.
    error: { type: String, default: "" },
    // "{vehicle} · {plate}" of the acceptance
    vehicle: { type: String, default: "" },
    // The request date (YYYY-MM-DD): the earliest receipt date.
    requestDate: { type: String, default: "" },
  },
  emits: ["update:modelValue", "submit"],
  data: () => ({
    form: emptyReceiptForm(),
    now: new Date(),
  }),
  computed: {
    maxNumber() {
      return RECEIPT_NUMBER_MAX;
    },
    accept() {
      return FILE_TYPES.join(",");
    },
    limits() {
      return receiptDateLimits({
        requestDate: this.requestDate,
        now: this.now,
      });
    },
    errors() {
      return receiptFormErrors(this.form, this.limits);
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    subtitle() {
      return [this.vehicle, this.$t("acceptance.pra.source_manual")]
        .filter(Boolean)
        .join(" · ");
    },
    dateHint() {
      return this.$t("acceptance.pra.receipt.dialog.date_hint", {
        date: formatCivilDate(this.limits.min) || "—",
      });
    },
    fileSize() {
      return this.form.file ? formatFileSize(this.form.file.size) : "";
    },
    fileProblem() {
      const reason = this.errors.file;
      if (!reason || reason === "required") return "";
      return this.$t(`acceptance.files.errors.${reason}`);
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.now = new Date();
      this.form = emptyReceiptForm();
    },
  },
  methods: {
    // An empty required field only disables the save; other problems show under the field.
    // Only "before_request" is the receipt's own; the others read as the CRD's.
    message(field) {
      const reason = this.errors[field];
      if (!reason || reason === "required") return [];
      const key =
        reason === "before_request"
          ? "acceptance.pra.receipt.dialog.errors.before_request"
          : `acceptance.pra.crd.dialog.errors.${reason}`;
      return [this.$t(key, { max: RECEIPT_NUMBER_MAX })];
    },
    pickFile() {
      this.$refs.fileInput.click();
    },
    onFile(event) {
      const [file] = event.target.files || [];
      if (file) this.form.file = file;
      // The same file can be picked again after a change.
      event.target.value = ""; // eslint-disable-line no-param-reassign
    },
    submit() {
      this.now = new Date();
      if (!this.valid || this.saving) return;
      this.$emit("submit", {
        number: this.form.number.trim(),
        date: this.form.date,
        file: this.form.file,
      });
    },
  },
};
</script>

<style scoped>
.pra-wrap {
  white-space: normal;
}
.pra-file-box {
  border: 1px dashed rgba(0, 0, 0, 0.24);
  border-radius: 4px;
}
</style>
